/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useState, useCallback, useEffect } from 'preact/hooks';
import { HTMLAttributes } from 'preact/compat';
import { classes, debounce } from '@adobe-commerce/elsie/lib';
import { Add, Minus } from '@adobe-commerce/elsie/icons';
import { Icon } from '@adobe-commerce/elsie/components/Icon';
import { Localizer, Text } from '@adobe-commerce/elsie/i18n';
import '@adobe-commerce/elsie/components/Incrementer/Incrementer.css';

export interface IncrementerProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'size'> {
  name?: string;
  size?: 'medium' | 'large';
  onValue?: (value: any) => void;
  onUpdateError?: (error: Error) => void;
  error?: boolean;
  success?: boolean;
  min?: number;
  max?: number;
  disabled?: boolean;
  maxLength?: number;
  showButtons?: boolean;
}

export const Incrementer: FunctionComponent<IncrementerProps> = ({
  name,
  value = '1',
  className,
  disabled,
  error,
  success,
  min,
  max,
  onValue,
  onUpdateError,
  size = 'medium',
  showButtons = true,
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState<number>(Number(value));
  const minValue = Number(min);
  const maxValue = Number(max);
  const isInvalid = error || currentValue < minValue || currentValue > maxValue;

  const errorMessage =
    currentValue < minValue
      ? 'Dropin.Incrementer.minQuantityMessage'
      : currentValue > maxValue
        ? 'Dropin.Incrementer.maxQuantityMessage'
        : 'Dropin.Incrementer.errorMessage';

  // Add this effect to synchronize internal state with external value prop
  useEffect(() => {
    const propValue = Number(value);
    if (propValue !== currentValue) {
      setCurrentValue(propValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnValueHandler = useCallback(
    debounce(async (newValue: any) => {
      if (onValue) {
        try {
          onValue(newValue);
        } catch (e) {
          if (onUpdateError) {
            onUpdateError(e as Error);
          }
        }
      }
    }, 200),
    [onValue, onUpdateError]
  );

  const handleIncrementer = (_value: number) => {
    let newValue = _value;
    debouncedOnValueHandler(newValue);
    setCurrentValue(newValue);
  };

  return (
    <div
      className={classes([
        'dropin-incrementer',
        `dropin-incrementer--${size}`,
        className,
      ])}
    >
      <div
        className={classes([
          'dropin-incrementer__content',
          `dropin-incrementer__content--${size}`,
          ['dropin-incrementer__content--no-buttons', !showButtons],
          [`dropin-incrementer__content--error`, isInvalid],
          [`dropin-incrementer__content--success`, success],
          [`dropin-incrementer__content--disabled`, disabled],
        ])}
      >
        {/* Minus Button */}
        {showButtons && (
          <div
            className={classes([
              'dropin-incrementer__button-container',
              [`dropin-incrementer__button-container--disabled`, disabled],
            ])}
          >
            <Localizer>
              <button
                type="button"
                className={classes([
                  'dropin-incrementer__decrease-button',
                  [`dropin-incrementer__decrease-button--disabled`, disabled],
                ])}
                onClick={() => handleIncrementer(currentValue - 1)}
                disabled={disabled || currentValue < minValue + 1}
                aria-label={
                  (<Text id="Dropin.Incrementer.decreaseLabel" />) as any
                }
              >
                <Icon
                  source={Minus}
                  size="16"
                  stroke="1"
                  viewBox="4 2 20 20"
                  className="dropin-incrementer__down"
                />
              </button>
            </Localizer>
          </div>
        )}

        {/* Input Field */}
        <input
          className="dropin-incrementer__input"
          max={max}
          min={min}
          step={1}
          type="number"
          name={name}
          value={currentValue}
          disabled={disabled}
          onBlur={() => {
            handleIncrementer(Number(currentValue));
          }}
          onChange={(e) => {
            const newValue = e.currentTarget.value;
            if (newValue !== '') {
              handleIncrementer(Number(newValue));
            }
          }}
          {...props}
        />

        {showButtons && (
          <div
            className={classes([
              'dropin-incrementer__button-container',
              [`dropin-incrementer__button-container--disabled`, disabled],
            ])}
          >
            {/* Plus/Add button */}
            <Localizer>
              <button
                type="button"
                className={classes([
                  'dropin-incrementer__increase-button',
                  [`dropin-incrementer__increase-button--disabled`, disabled],
                ])}
                onClick={() => handleIncrementer(currentValue + 1)}
                disabled={disabled || currentValue > maxValue - 1}
                aria-label={
                  (<Text id="Dropin.Incrementer.increaseLabel" />) as any
                }
              >
                <Icon
                  source={Add}
                  size="16"
                  stroke="1"
                  viewBox="4 2 20 20"
                  className="dropin-incrementer__add"
                />
              </button>
            </Localizer>
          </div>
        )}
      </div>
      {isInvalid && (
        <p className="dropin-incrementer__content--error-message">
          <Text
            id={errorMessage}
            fields={{ minQuantity: min, maxQuantity: max }}
          />
        </p>
      )}
    </div>
  );
};
